/**
 * Left rail for the POS Styles Workshop layout.
 *
 * Sticky vertical sidebar shown only on ≥2xl screens (1536px+). Provides:
 *   • Garment-jump pills (Jacket / Pant / Vest / Shirt / Overcoat) — scrolls
 *     the center pane to the matching styling section.
 *   • Quick reference of the current Make + Construction Type.
 *   • Tip blurb about the Style Cart on the right rail.
 *
 * Garment-jump pills only render when the parent includes that garment in
 * the order (e.g. a Trousers-only order skips Jacket / Vest / Shirt).
 *
 * Props:
 *   includes        { jacket, trouser, vest, shirt, overcoat }  — booleans
 *   make            "rc" | "ctd"
 *   constructionType  the selected construction type string
 *   activeGarment   currently in-view garment id (for highlight)
 *   onJump          (garmentId) => void
 */
import { Shirt, Scissors, Layers, Ruler, Sparkles } from "lucide-react";
import { MAKE_PROFILES } from "@/lib/makePricing";

const PILLS = [
  { id: "jacket",   label: "Jacket",   color: "amber",   icon: Shirt,    flag: "jacket"   },
  { id: "pant",     label: "Trouser",  color: "blue",    icon: Scissors, flag: "trouser"  },
  { id: "vest",     label: "Vest",     color: "purple",  icon: Layers,   flag: "vest"     },
  { id: "shirt",    label: "Shirt",    color: "rose",    icon: Shirt,    flag: "shirt"    },
];

const COLOR_CLASSES = {
  amber:  "border-amber-500/40 text-amber-300 bg-amber-500/10 hover:bg-amber-500/20",
  blue:   "border-blue-500/40 text-blue-300 bg-blue-500/10 hover:bg-blue-500/20",
  purple: "border-purple-500/40 text-purple-300 bg-purple-500/10 hover:bg-purple-500/20",
  rose:   "border-rose-500/40 text-rose-300 bg-rose-500/10 hover:bg-rose-500/20",
};

const INACTIVE = "border-white/10 text-slate-400 bg-[#0a1628] hover:text-white hover:border-white/20";

export default function GarmentJumpNav({
  includes = {},
  make = "rc",
  constructionType = "",
  activeGarment = "",
  onJump,
  className = "",
}) {
  const visible = PILLS.filter((p) => includes[p.flag]);
  return (
    <aside
      data-testid="pos-workshop-left-rail"
      className={`hidden 2xl:flex flex-col gap-4 sticky top-4 self-start h-fit ${className}`}
    >
      {/* Header */}
      <div className="px-3 py-2">
        <p className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
          <Sparkles size={11} className="text-amber-400" /> Workshop View
        </p>
        <p className="text-sm font-semibold text-slate-200 mt-0.5">Garments</p>
      </div>

      {/* Garment Jump Pills */}
      <nav className="flex flex-col gap-2 px-2" aria-label="Jump to garment section">
        {visible.length === 0 && (
          <p className="text-xs text-slate-500 px-2">Pick a garment type to start styling.</p>
        )}
        {visible.map((p) => {
          const Icon = p.icon;
          const active = activeGarment === p.id;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => onJump?.(p.id)}
              data-testid={`pos-workshop-jump-${p.id}`}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-all ${
                active ? COLOR_CLASSES[p.color] : INACTIVE
              }`}
            >
              <Icon size={14} />
              <span>{p.label}</span>
            </button>
          );
        })}
      </nav>

      {/* Make + Construction summary */}
      <div className="mx-2 mt-2 px-3 py-3 rounded-lg bg-[#0a1628] border border-white/10">
        <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Pipeline</p>
        <div className="flex items-center gap-1.5 text-xs">
          <span
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: MAKE_PROFILES[make]?.color || "#3b82f6" }}
          />
          <span className="font-medium" style={{ color: MAKE_PROFILES[make]?.color || "#3b82f6" }}>
            {MAKE_PROFILES[make]?.label || "RC Make"}
          </span>
        </div>
        <p className="text-[11px] text-slate-400 mt-1.5">{constructionType || "—"}</p>
      </div>

      {/* Hint footer */}
      <div className="mx-2 mt-1 px-3 py-2 rounded-lg bg-[#0a1628]/60 border border-dashed border-white/10">
        <p className="text-[10px] text-slate-500 leading-relaxed">
          <Ruler size={10} className="inline -mt-0.5 mr-1 text-amber-400/70" />
          Style picks update the <span className="text-amber-300/80">Style Cart</span> on the right.
        </p>
      </div>
    </aside>
  );
}
